import authApi from '@/api/auth-api'
import constants from '@/constants'
import { setAuthError } from '@/utils/auth'
import cmsApi from '@/api/cms-api'
import { setLocalStorageAppConfiguration, getLocalStorageAppConfiguration,
  getCondensedBannersConfigResponse, setSessionUpdatedDate, emitEventToPiniaStore } from '@/utils/common'
import { clearSessionUpdatedDate, getLocalStorageItem, removeLocalStorageItem } from '../utils/common'

export default {
  state: {
    userDetails: {},
    isPasswordSaved: false,
    isUserFetchFinished: false,
    isSendEmailBtnClicked: false,
    isLoggedIn: false,
    visiblePasswordStrength: false,
    isEmailVerification: false,
    isVerificationLinkExpired: true,
    isPasswordVerificationLinkExpired: true,
    resetPasswordResponse: '',
    appConfigs: null,
    isAppConfigFetched: false,
    banners: [],
    isSessionAPIUpdateCallInProgress: false,
    currentCompanyId: null,
    currentCompanyInfo: {},
    tenantList: [],
    currentTenantId: ''
  },
  getters: {
    getUserDetails (state) {
      return state.userDetails
    },
    getIsPasswordSaved (state) {
      return state.isPasswordSaved
    },
    getIsUserFetchFinished (state) {
      return state.isUserFetchFinished
    },
    getIsSendEmailBtnClicked (state) {
      return state.isSendEmailBtnClicked
    },
    getIsLoggedIn (state) {
      return state.isLoggedIn
    },
    getVisiblePasswordStrength (state) {
      return state.visiblePasswordStrength
    },
    getIsEmailVerification (state) {
      return state.isEmailVerification
    },
    getIsVerificationLinkExpired (state) {
      return state.isVerificationLinkExpired
    },
    getIsPasswordVerificationLinkExpired (state) {
      return state.isPasswordVerificationLinkExpired
    },
    getResetPasswordResponse (state) {
      return state.resetPasswordResponse
    },
    getAppConfigs (state) {
      return state.appConfigs
    },
    getIsAppConfigFetched (state) {
      return state.isAppConfigFetched
    },
    getBanners (state) {
      return state.banners
    },
    getIsSessionAPIUpdateCallInProgress (state) {
      return state.isSessionAPIUpdateCallInProgress
    },
    getRegistrationId (state) {
      return state.userDetails?.id
    },
    getDealerId (state) {
      return state.userDetails?.companyDetails?.dealerId || ''
    },
    getUserEmailId (state) {
      return state.userDetails.email
    },
    getIsAccountRejected (state) {
      return state.userDetails?.companyDetails?.companyRegistrationStatus === constants.companyRegistrationStatus.REJECTED
    },
    getIsAccountRejectedReason (state) {
      return state.userDetails?.companyDetails?.registrationRejectedReason || ''
    },
    getFinanceRejectedReason (state) {
      return state.userDetails?.companyDetails?.financeRegistrationRejectedReason || ''
    },
    getCreditLimitDetails (state) {
      return {
        limitValue: state.userDetails?.companyDetails?.creditLimitValue,
        paymentMethod: state.userDetails?.companyDetails?.creditLimitTermsOfPayment
      }
    },
    getCurrentCompanyID (state) {
      return state.currentCompanyId
    },
    getCurrentCompanyInfo (state) {
      return state.currentCompanyInfo
    },
    getCurrentTenantId (state) {
      return state.currentTenantId
    }
  },
  mutations: {
    setUserDetails (state, value) {
      emitEventToPiniaStore('VUEX_USER_DETAILS', value)
      state.userDetails = value
    },
    setUserDetailsFromPinia (state, value) {
      state.userDetails = value
    },
    setIsPasswordSaved (state, value) {
      state.isPasswordSaved = value
    },
    setIsUserFetchFinished (state, value) {
      emitEventToPiniaStore('VUEX_IS_USER_FETCH_FINISHED', value)
      state.isUserFetchFinished = value
    },
    setIsSendEmailBtnClicked (state, value) {
      state.isSendEmailBtnClicked = value
    },
    setIsLoggedIn (state, value) {
      emitEventToPiniaStore('VUEX_IS_LOGGED_IN', value)
      state.isLoggedIn = value
    },
    setVisiblePasswordStrength (state, value) {
      state.visiblePasswordStrength = value
    },
    setIsEmailVerification (state, value) {
      state.isEmailVerification = value
    },
    setIsVerificationLinkExpired (state, value) {
      state.isVerificationLinkExpired = value
    },
    setIsPasswordVerificationLinkExpired (state, value) {
      state.isPasswordVerificationLinkExpired = value
    },
    setResetPasswordResponse (state, value) {
      state.resetPasswordResponse = value
    },
    setAppConfigs (state, value) {
      emitEventToPiniaStore('VUEX_APP_CONFING', value)
      state.appConfigs = value
    },
    setIsAppConfigFetched (state, value) {
      emitEventToPiniaStore('VUEX_IS_APP_CONFIG_FETCHED', value)
      state.isAppConfigFetched = value
    },
    setBanners (state, value) {
      state.banners = value
    },
    setIsSessionAPIUpdateCallInProgress (state, value) {
      state.isSessionAPIUpdateCallInProgress = value
    },
    updateRegistrationStep (state, value) {
      state.userDetails = {
        ...state.userDetails,
        companyDetails: {
          ...state.userDetails.companyDetails,
          registrationNextStep: value
        }
      }
    },
    setIsUserFetchFinishedFromPinia (state, value) {
      state.isUserFetchFinished = value
    },
    setisLoggedInFromPinia (state, value) {
      state.isLoggedIn = value
    },
    setCurrentCompanyId (state, value) {
      emitEventToPiniaStore('VUEX_CURRENT_COMPANY_ID', value)
      state.currentCompanyId = value
    },
    setCurrentCompanyIdFromPinia (state, value) {
      state.currentCompanyId = value
    },
    setCurrentCompanyInfo (state, value) {
      emitEventToPiniaStore('VUEX_UPDATE_CURRENT_COMPANY_INFO', value)
      state.currentCompanyInfo = value
    },
    setCurrentCompanyInfoFromPinia (state, value) {
      state.currentCompanyInfo = value
    },
    setCurrentTenantId (state, value) {
      emitEventToPiniaStore('VUEX_UPDATE_CURRENT_TENANT_ID', value)
      state.currentTenantId = value
    },
    setTenantList (state, value) {
      emitEventToPiniaStore('VUEX_UPDATE_TENANT_LIST', value)
      state.tenantList = value
    },
    setTenantListFromPinia (state, value) {
      state.tenantList = value
    },
    setTenantIdFromPinia (state, value) {
      state.currentTenantId = value
    }
  },
  actions: {
    POST_VERIFICATION_EMAIL (_, { query, success, fail }) {
      authApi.sendVerificationEmail(query)
        .then(res => {
          if (res.body.success) {
            success && success(res.body)
          } else {
            fail && fail(res.body)
          }
        })
        .catch(err => {
          fail && fail(err)
        })
    },
    GET_APP_CONFIGURATION ({ state, commit }, { query, success, fail }) {
      cmsApi.getConfig(query).then(response => {
        commit('setIsAppConfigFetched', true)
        if (response.body?.code === 200) {
          if (query?.id === 'applereseller.ui.config') {
            commit('setAppConfigs', JSON.parse(response.body?.data?.value))
            success && success(state.appConfigs)
          }
        } else {
          fail && fail(response.body)
        }
      }).catch(err => {
        commit('setIsAppConfigFetched', true)
        fail && fail(err)
      })
    },
    GET_BANNERS_AND_SERVICES ({ commit }) {
      cmsApi.getServicesPromotion().then(response => {
        if (response.body.code === 200) {
          const mainResponse = response.body?.data?.[0]
          const bannerConfig = getCondensedBannersConfigResponse(mainResponse?.blocks?.[0]?.components)
          commit('setBanners', bannerConfig)
          setLocalStorageAppConfiguration('appleResellerBannersConfig', JSON.stringify(bannerConfig))
        } else {
          const getBannersConfig = getLocalStorageAppConfiguration('appleResellerBannersConfig')
          if (getBannersConfig) {
            commit('setBanners', JSON.parse(getBannersConfig))
          }
        }
      },
      () => {
        const getBannersConfig = getLocalStorageAppConfiguration('appleResellerBannersConfig')
        if (getBannersConfig) {
          commit('setBanners', JSON.parse(getBannersConfig))
        }
      })
    },
    UPDATE_SESSION ({ state, commit }) {
      if (state.isSessionAPIUpdateCallInProgress) {
        return
      }
      commit('setIsSessionAPIUpdateCallInProgress', true)
      authApi.sessionUpdate()
        .then(() => {
          setSessionUpdatedDate()
          commit('setIsSessionAPIUpdateCallInProgress', false)
        }).catch(() => {
          commit('setIsSessionAPIUpdateCallInProgress', false)
          clearSessionUpdatedDate()
        })
    },
    VERIFY_USER (_, { data, success, fail }) {
      authApi.userLogin(data)
        .then((res) => {
          success && success(res.body)
        }).catch(err => {
          fail && fail(err)
        })
    },
    LOGOUT_USER ({ commit }, { success, fail }) {
      authApi.userLogout().then(() => {
        commit('setUserDetails', {})
        commit('setCurrentCompanyId', null)
        commit('setCurrentCompanyInfo', {})
        commit('setIsLoggedIn', false)
        setAuthError(401)
        clearSessionUpdatedDate()
        success && success()
        const isInternalUser = getLocalStorageItem('isInternalUser')
        if (isInternalUser) {
          removeLocalStorageItem('isInternalUser')
          location.reload()
        }
      }).catch(() => {
        fail && fail()
      })
    },
    CREATE_USER (_, { data, success, fail }) {
      authApi.userSignup(data)
        .then((res) => {
          success && success(res.body)
        }).catch(err => {
          fail && fail(err)
        })
    },
    GET_USER_INFO ({ state, commit }, { success, fail } = {}) {
      authApi.getUserDetails()
        .then((res) => {
          commit('setUserDetails', res.body.value)
          commit('setIsLoggedIn', true)
          commit('setIsUserFetchFinished', true)
          setSessionUpdatedDate()
          const { companyDetails = null, tenantDetailsDTOS = [] } = state.userDetails
          commit('setCurrentCompanyId', companyDetails?.companyId || '')
          commit('setCurrentCompanyInfo', state.userDetails?.companyDetails || {})
          commit('setTenantList', tenantDetailsDTOS)
          commit('setCurrentTenantId', companyDetails?.tenantId || '')

          if (companyDetails?.showAgreementPopup && companyDetails?.agreementId && companyDetails?.dealerId) {
            emitEventToPiniaStore('VUEX_AGREEMENT_POPUP', {
              dealerId: companyDetails.dealerId,
              agreementId: companyDetails.agreementId
            })
          }

          emitEventToPiniaStore('VUEX_UPDATE_THEME', res.body.value.theme)
          emitEventToPiniaStore('VUEX_NOTIFICATION_PERMISSION')
          emitEventToPiniaStore('VUEX_GET_TOKEN')
          setAuthError(false)
          success && success(res.body.value)
        }).catch(err => {
          commit('setUserDetails', {})
          commit('setIsLoggedIn', false)
          commit('setIsUserFetchFinished', true)
          commit('setCurrentCompanyId', null)
          commit('setCurrentCompanyInfo', {})
          commit('setTenantList', [])
          commit('setCurrentTenantId', '')
          fail && fail(err)
        })
    },
    VERIFY_EMAIL_WITH_ID (_, { data, success, fail }) {
      authApi.verifyEmailWithId(data)
        .then((res) => {
          success && success(res.body)
        }).catch(err => {
          fail && fail(err)
        })
    },
    POST_FORGOT_PASSWORD_INFO (_, { data, success, fail }) {
      authApi.postForgotPasswordInfo(data)
        .then(res => {
          if (res.body.success) {
            success && success(res.body)
          } else {
            fail && fail(res.body)
          }
        })
        .catch(err => {
          fail && fail(err)
        })
    },
    POST_RESET_PASSWORD_INFO (_, { data, success, fail }) {
      authApi.postResetPasswordInfo(data)
        .then(res => {
          success && success(res.body)
        })
        .catch(err => {
          fail && fail(err)
        })
    },
    POST_VERIFY_PASSWORD_ID (_, { data, success, fail }) {
      authApi.postVerifyPasswordWithId(data)
        .then(res => {
          success && success(res.body)
        })
        .catch(err => {
          fail && fail(err)
        })
    }
  },
  namespaced: false
}
